import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Computer as WindowsIcon,
  LaptopMac as AppleIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface DesktopAppDownloadProps {
  onDownloadComplete?: () => void;
}

interface VersionInfo {
  latest_version: string;
  update_available: boolean;
  update_url: string;
  release_notes?: string;
  critical_update: boolean;
  published_at?: string;
}

const DesktopAppDownload: React.FC<DesktopAppDownloadProps> = ({ onDownloadComplete }) => {
  const { token } = useAuth();
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [platform, setPlatform] = useState<'windows' | 'macos'>('windows');

  useEffect(() => {
    // Detect platform
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMac = userAgent.includes('mac');
      setPlatform(isMac ? 'macos' : 'windows');
    } else {
      // Default to windows in test environment
      setPlatform('windows');
    }
  }, []);

  const fetchVersionInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      // For now, use static download URLs as fallback
      // In production, this would call /api/v1/desktop/version
      // Use setTimeout to simulate async operation for proper React state updates
      await new Promise(resolve => setTimeout(resolve, 0));
      
      const defaultVersionInfo: VersionInfo = {
        latest_version: '1.0.0',
        update_available: true,
        update_url: platform === 'windows' 
          ? 'https://download.example.com/tos-autotrade-windows-latest.msi'
          : 'https://download.example.com/tos-autotrade-macos-latest.dmg',
        release_notes: 'Initial release with toast notification capture',
        critical_update: false,
        published_at: new Date().toISOString(),
      };

      // TODO: Call actual API endpoint when available
      // const response = await apiService.get(`/api/v1/desktop/version?platform=${platform}`, token);
      // if (response.success) {
      //   setVersionInfo(response.data);
      // } else {
      //   setVersionInfo(defaultVersionInfo);
      // }
      
      setVersionInfo(defaultVersionInfo);
    } catch (err) {
      console.error('Error fetching version info:', err);
      setError('Failed to load version information');
    } finally {
      setLoading(false);
    }
  }, [platform, token]);

  useEffect(() => {
    fetchVersionInfo();
  }, [fetchVersionInfo]);

  const handleDownload = async () => {
    if (!versionInfo) return;

    try {
      setDownloading(true);
      setError(null);

      // Open download URL in new window
      window.open(versionInfo.update_url, '_blank');
      
      // Simulate download completion after a delay
      setTimeout(() => {
        setDownloading(false);
        onDownloadComplete?.();
      }, 2000);
    } catch (err) {
      console.error('Error downloading:', err);
      setError('Failed to start download');
      setDownloading(false);
    }
  };

  const handleReinstall = () => {
    handleDownload();
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Download Desktop App
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {versionInfo && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" gutterBottom>
                Get the full desktop experience with native toast notification capture.
              </Typography>
              
              <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Chip 
                  icon={platform === 'windows' ? <WindowsIcon /> : <AppleIcon />}
                  label={`Version ${versionInfo.latest_version}`}
                  color="primary"
                />
                {versionInfo.critical_update && (
                  <Chip label="Critical Update" color="error" size="small" />
                )}
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleDownload}
                disabled={downloading}
                fullWidth
                sx={{ mb: 2 }}
              >
                {downloading ? 'Downloading...' : `Download for ${platform === 'windows' ? 'Windows' : 'macOS'}`}
              </Button>

              <Button
                variant="outlined"
                size="medium"
                startIcon={<RefreshIcon />}
                onClick={handleReinstall}
                disabled={downloading}
                fullWidth
              >
                Reinstall Desktop App
              </Button>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                System Requirements
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={platform === 'windows' ? 'Windows 10 or Windows 11' : 'macOS 11.0 or later'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Internet connection for webhook execution" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Signal source application installed" />
                </ListItem>
              </List>
            </Box>

            {versionInfo.release_notes && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Release Notes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {versionInfo.release_notes}
                </Typography>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DesktopAppDownload;

